/**
 * Copyright (c) 2020-2021 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { ParamDefinition as PD } from '../../../mol-util/param-definition';
import { StructureElement, Bond } from '../../../mol-model/structure';
import { Vec3 } from '../../../mol-math/linear-algebra';
import { BitFlags, arrayEqual } from '../../../mol-util';
import { createLinkLines } from './util/link';
import { ComplexLinesVisual, ComplexLinesParams } from '../complex-visual';
import { BondType } from '../../../mol-model/structure/model/types';
import { BondIterator, getInterBondLoci, eachInterBond, BondLineParams, makeInterBondIgnoreTest } from './util/bond';
import { Lines } from '../../../mol-geo/geometry/lines/lines';
import { Sphere3D } from '../../../mol-math/geometry';
var tmpRefPosBondIt = new Bond.ElementBondIterator();

function setRefPosition(pos, structure, unit, index) {
  tmpRefPosBondIt.setElement(structure, unit, index);

  while (tmpRefPosBondIt.hasNext) {
    var bA = tmpRefPosBondIt.move();
    bA.otherUnit.conformation.position(bA.otherUnit.elements[bA.otherIndex], pos);
    return pos;
  }

  return null;
}

function createInterUnitBondLines(ctx, structure, theme, props, lines) {
  var bonds = structure.interUnitBonds;
  var edgeCount = bonds.edgeCount,
      edges = bonds.edges;
  var sizeFactor = props.sizeFactor,
      aromaticBonds = props.aromaticBonds;
  if (!edgeCount) return Lines.createEmpty(lines);
  var ref = Vec3();
  var loc = StructureElement.Location.create();
  var builderProps = {
    linkCount: edgeCount,
    referencePosition: function (edgeIndex) {
      var b = edges[edgeIndex];
      var unitA, unitB;
      var indexA, indexB;

      if (b.unitA < b.unitB) {
        unitA = structure.unitMap.get(b.unitA);
        unitB = structure.unitMap.get(b.unitB);
        indexA = b.indexA;
        indexB = b.indexB;
      } else if (b.unitA > b.unitB) {
        unitA = structure.unitMap.get(b.unitB);
        unitB = structure.unitMap.get(b.unitA);
        indexA = b.indexB;
        indexB = b.indexA;
      } else {
        throw new Error('same units in createInterUnitBondLines');
      }

      return setRefPosition(ref, structure, unitA, indexA) || setRefPosition(ref, structure, unitB, indexB);
    },
    position: function (posA, posB, edgeIndex) {
      var b = edges[edgeIndex];
      var uA = structure.unitMap.get(b.unitA);
      var uB = structure.unitMap.get(b.unitB);
      uA.conformation.position(uA.elements[b.indexA], posA);
      uB.conformation.position(uB.elements[b.indexB], posB);
    },
    style: function (edgeIndex) {
      var o = edges[edgeIndex].props.order;
      var f = BitFlags.create(edges[edgeIndex].props.flag);

      if (BondType.is(f, 2
      /* MetallicCoordination */
      ) || BondType.is(f, 4
      /* HydrogenBond */
      )) {
        // show metall coordinations and hydrogen bonds with dashed cylinders
        return 1
        /* Dashed */
        ;
      } else if (o === 3) {
        return 3
        /* Triple */
        ;
      } else if (aromaticBonds && BondType.is(f, 16
      /* Aromatic */
      )) {
        return 5
        /* Aromatic */
        ;
      } else if (o === 2) {
        return 2
        /* Double */
        ;
      }

      return 0
      /* Solid */
      ;
    },
    radius: function (edgeIndex) {
      var b = edges[edgeIndex];
      loc.structure = structure;
      loc.unit = structure.unitMap.get(b.unitA);
      loc.element = loc.unit.elements[b.indexA];
      var sizeA = theme.size.size(loc);
      loc.unit = structure.unitMap.get(b.unitB);
      loc.element = loc.unit.elements[b.indexB];
      var sizeB = theme.size.size(loc);
      return Math.min(sizeA, sizeB) * sizeFactor;
    },
    ignore: makeInterBondIgnoreTest(structure, props)
  };
  var l = createLinkLines(ctx, builderProps, props, lines);
  var child = structure.child;
  var sphere = Sphere3D.expand(Sphere3D(), (child !== null && child !== void 0 ? child : structure).boundary.sphere, 1 * props.sizeFactor);
  l.setBoundingSphere(sphere);
  return l;
}

export var InterUnitBondLineParams = __assign(__assign(__assign({}, ComplexLinesParams), BondLineParams), {
  includeParent: PD.Boolean(false)
});
export function InterUnitBondLineVisual(materialId) {
  return ComplexLinesVisual({
    defaultProps: PD.getDefaultValues(InterUnitBondLineParams),
    createGeometry: createInterUnitBondLines,
    createLocationIterator: BondIterator.fromStructure,
    getLoci: getInterBondLoci,
    eachLocation: eachInterBond,
    setUpdateState: function (state, newProps, currentProps) {
      state.createGeometry = newProps.sizeFactor !== currentProps.sizeFactor || newProps.linkScale !== currentProps.linkScale || newProps.linkSpacing !== currentProps.linkSpacing || newProps.dashCount !== currentProps.dashCount || newProps.ignoreHydrogens !== currentProps.ignoreHydrogens || !arrayEqual(newProps.includeTypes, currentProps.includeTypes) || !arrayEqual(newProps.excludeTypes, currentProps.excludeTypes);
    }
  }, materialId);
}